import React from 'react'
import { Show, TextField, DateField } from '@refinedev/antd'
import { useShow, useOne, useUpdate } from '@refinedev/core'
import { App, Image, Select, Space, Typography } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { Pill, type Tone } from '../../theme/listkit'

type Photo = Tables<'photos'>
type Album = Tables<'albums'>

const { Title } = Typography

interface ModerationLabel {
  name: string
  confidence: number
  parent?: string
  level?: string
}

type ModerationStatus = 'approved' | 'flagged' | 'rejected' | 'pending'

const moderationTone = (status: string): Tone => {
  if (status === 'approved') return 'green'
  if (status === 'flagged') return 'gold'
  if (status === 'rejected') return 'red'
  return 'neutral'
}

const labelStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 4,
}

export function PhotoShow() {
  const { query } = useShow<Photo>()
  const isLoading = query.isLoading
  const record = query.data?.data

  const { query: albumQuery } = useOne<Album>({
    resource: 'albums',
    id: record?.album_id ?? '',
    queryOptions: { enabled: !!record?.album_id },
  })

  const { mutate: update } = useUpdate()
  const [overriding, setOverriding] = React.useState(false)
  const { message } = App.useApp()

  const handleOverride = (newStatus: ModerationStatus) => {
    if (!record) return
    setOverriding(true)
    update(
      {
        resource: 'photos',
        id: record.id,
        values: { moderation_status: newStatus },
      },
      {
        onSuccess: () => {
          setOverriding(false)
          message.success(`Photo moderation status set to "${newStatus}"`)
        },
        onError: (e) => {
          setOverriding(false)
          message.error('Update failed: ' + String(e))
        },
      },
    )
  }

  // Parse moderation_labels — stored as JSON, may be array or null-ish
  const moderationLabels: ModerationLabel[] = React.useMemo(() => {
    const raw = record?.moderation_labels
    if (!raw) return []
    if (Array.isArray(raw)) return raw as unknown as ModerationLabel[]
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
      return Array.isArray(parsed) ? (parsed as unknown as ModerationLabel[]) : []
    } catch {
      return []
    }
  }, [record?.moderation_labels])

  return (
    <Show isLoading={isLoading}>
      <Title level={5}>Album</Title>
      <TextField value={albumQuery.data?.data?.name ?? record?.album_id ?? '—'} />

      <Title level={5}>Label</Title>
      <TextField value={record?.label ?? '—'} />

      <Title level={5}>Image</Title>
      {record?.url ? (
        <Image
          src={record.url}
          alt={record.label}
          width={300}
          style={{ borderRadius: 4 }}
          preview={{ src: record.url }}
        />
      ) : (
        <TextField value="No image" />
      )}

      <Title level={5}>URL</Title>
      <TextField value={record?.url ?? '—'} />

      <Title level={5}>Created</Title>
      {record?.created_at ? (
        <DateField value={record.created_at} format="MMM D, YYYY h:mm A" />
      ) : (
        <TextField value="—" />
      )}

      {/* ── Image moderation section ── */}
      <div
        style={{
          marginTop: 24,
          padding: '16px 20px',
          background: brand.paper,
          border: '1px solid ' + brand.cardLine,
          borderRadius: 12,
        }}
      >
        <div
          style={{
            fontFamily: brand.fDisplay,
            fontWeight: 700,
            fontSize: 14,
            color: brand.ink,
            marginBottom: 14,
          }}
        >
          Image Moderation (Rekognition)
        </div>

        {/* Current status + admin override */}
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Moderation Status</div>
          <Space align="center" size={10}>
            <Pill tone={moderationTone(record?.moderation_status ?? 'pending')}>
              {record?.moderation_status ?? 'pending'}
            </Pill>
            <Select
              value={record?.moderation_status as ModerationStatus | undefined}
              placeholder="Override status…"
              style={{ width: 180 }}
              loading={overriding}
              disabled={overriding || !record}
              onChange={handleOverride}
              aria-label="Override moderation status"
              options={[
                { label: 'Approved', value: 'approved' },
                { label: 'Flagged', value: 'flagged' },
                { label: 'Rejected', value: 'rejected' },
                { label: 'Pending', value: 'pending' },
              ]}
            />
          </Space>
          <div
            style={{
              fontFamily: brand.fSans,
              fontSize: 11.5,
              color: brand.sub,
              marginTop: 5,
            }}
          >
            Manually override the Rekognition verdict to correct false positives or escalate.
          </div>
        </div>

        {/* Moderated at timestamp */}
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Moderated At</div>
          <div
            style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.ink }}
          >
            {record?.moderated_at
              ? new Date(record.moderated_at).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '—'}
          </div>
        </div>

        {/* Rekognition findings table */}
        <div>
          <div style={labelStyle}>Rekognition Findings</div>
          {moderationLabels.length === 0 ? (
            <div style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub }}>
              No findings recorded.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: '6px 16px',
                alignItems: 'center',
              }}
            >
              {/* Header */}
              <div style={{ ...labelStyle, marginBottom: 0 }}>Label</div>
              <div style={{ ...labelStyle, marginBottom: 0 }}>Confidence</div>
              <div style={{ ...labelStyle, marginBottom: 0 }}>Parent</div>
              {/* Rows */}
              {moderationLabels.map((lbl, i) => (
                <React.Fragment key={i}>
                  <div
                    style={{
                      fontFamily: brand.fSans,
                      fontWeight: 600,
                      fontSize: 13,
                      color: brand.ink,
                    }}
                  >
                    {lbl.name}
                  </div>
                  <div
                    style={{
                      fontFamily: brand.fSans,
                      fontWeight: 500,
                      fontSize: 13,
                      color: lbl.confidence >= 80 ? '#a4452f' : brand.sub,
                      textAlign: 'right',
                    }}
                  >
                    {lbl.confidence?.toFixed(1)}%
                  </div>
                  <div
                    style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}
                  >
                    {lbl.parent ?? '—'}
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </Show>
  )
}
